import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService, Toast, ToastType } from '../services/toast.service';

@Component({
  selector: 'app-toast-notification',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Toast Container - Fixed top-right -->
    <div class="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
      @for (toast of toastService.toasts(); track toast.id) {
        <div
          [attr.data-toast-id]="toast.id"
          [class]="getToastClasses(toast.type) + ' toast-item pointer-events-auto min-w-[320px] max-w-md shadow-lg rounded-lg overflow-hidden'"
        >
          <!-- Toast Content -->
          <div class="flex items-start p-4 gap-3">
            <!-- Icon -->
            <div [class]="getIconContainerClasses(toast.type) + ' flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center'">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                @switch (toast.type) {
                  @case ('success') {
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  }
                  @case ('error') {
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  }
                  @case ('warning') {
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  }
                  @case ('info') {
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  }
                }
              </svg>
            </div>

            <!-- Message Content -->
            <div class="flex-1 pt-0.5">
              @if (toast.title) {
                <h3 class="text-sm font-semibold text-gray-900 mb-1">{{ toast.title }}</h3>
              }
              <p class="text-sm text-gray-700">{{ toast.message }}</p>
            </div>

            <!-- Close Button -->
            @if (toast.dismissible) {
              <button
                (click)="closeToast(toast.id)"
                class="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            }
          </div>

          <!-- Progress Bar (Auto-dismiss indicator) -->
          @if (toast.duration && toast.duration > 0) {
            <div class="h-1 bg-gray-200">
              <div
                [class]="getProgressBarClasses(toast.type) + ' h-full progress-bar'"
                [style.animation-duration.ms]="toast.duration"
              ></div>
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    /* Slide-in animation from right */
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    /* Fade-out animation */
    @keyframes fadeOut {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
        transform: translateX(20px);
      }
    }

    /* Progress bar countdown animation */
    @keyframes progress {
      from {
        width: 100%;
      }
      to {
        width: 0%;
      }
    }

    .toast-item {
      animation: slideInRight 0.3s ease-out;
    }

    .progress-bar {
      animation: progress linear;
    }
  `]
})
export class ToastNotificationComponent {
  toastService = inject(ToastService);

  /**
   * Get Tailwind classes for toast background based on type
   */
  getToastClasses(type: ToastType): string {
    const baseClasses = 'bg-white border-l-4';

    switch (type) {
      case 'success':
        return `${baseClasses} border-green-500`;
      case 'error':
        return `${baseClasses} border-[#FF4545]`; // Brand red
      case 'warning':
        return `${baseClasses} border-[#FAA030]`; // Brand orange
      case 'info':
        return `${baseClasses} border-blue-500`;
      default:
        return baseClasses;
    }
  }

  /**
   * Get icon container classes with gradient backgrounds
   */
  getIconContainerClasses(type: ToastType): string {
    switch (type) {
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-gradient-to-r from-[#FF4545] to-[#FAA030]'; // Brand gradient
      case 'warning':
        return 'bg-gradient-to-r from-[#FAA030] to-[#FF4545]'; // Reversed gradient
      case 'info':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  }

  /**
   * Get progress bar classes
   */
  getProgressBarClasses(type: ToastType): string {
    switch (type) {
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-[#FF4545]';
      case 'warning':
        return 'bg-[#FAA030]';
      case 'info':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  }

  /**
   * Close a toast manually
   */
  closeToast(id: string): void {
    this.toastService.remove(id);
  }
}
